import { devLog } from '../../utils/devLog';

import React, { useState } from 'react';
import { useAppContext } from '../../hooks/useAppContext';
import { useToast } from '../common/Toast';
import { addQuestionToRole } from '../../utils/api';

const QuestionForm = () => {
  const { jobs, addQuestion } = useAppContext();
  const { showSuccess, showError } = useToast();
  const [selectedJobId, setSelectedJobId] = useState('');
  const [formData, setFormData] = useState({
    text: '',
    type: 'Technical',
    time: 120,
    weight: 5
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedJobId) {
      showError('Please select a job position first!', 4000);
      return;
    }

    if (!formData.text.trim()) {
      showError('Please enter question text', 4000);
      return;
    }

    setIsSubmitting(true);

    try {
      // Call API to add question to role
      const result = await addQuestionToRole(selectedJobId, {
        text: formData.text,
        type: formData.type,
        time: parseInt(formData.time),
        weight: parseInt(formData.weight)
      });

      if (result.success) {
        // Also add to local state for immediate UI update
        addQuestion({
          ...formData,
          id: result.data.data.questionId,
          jobId: selectedJobId,
          createdAt: new Date().toISOString()
        });

        // Reset form
        setFormData({
          text: '',
          type: 'Technical',
          time: 120,
          weight: 5
        });

        const totalQuestions = result.data?.data?.totalQuestions ?? 0;
        showSuccess(`Question added successfully! Total questions: ${totalQuestions}`, 5000);
      } else {
        showError(result.error || 'Failed to add question', 6000);
      }
    } catch (error) {
      devLog.error('Error adding question:', error);
      showError('Failed to add question. Please try again.', 6000);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Filter to show only Open (active) jobs
  const openJobs = jobs.filter(job => job.status === 'Open');

  return (
    <div>
      <div className="form-group">
        <label className="form-label">Select Job Position *</label>
        <select 
          className="form-input"
          value={selectedJobId}
          onChange={(e) => setSelectedJobId(e.target.value)}
        >
          <option value="">-- Select a job position --</option>
          {openJobs.length === 0 ? (
            <option value="" disabled>No active job positions available</option>
          ) : (
            openJobs.map(job => (
              <option key={job.id} value={job.id}>{job.title}</option>
            ))
          )}
        </select>
      </div>

      {selectedJobId && (
        <form onSubmit={handleSubmit}>
          <h3 style={{ color: '#4a5568', marginBottom: '20px', marginTop: '20px' }}>
            Add New Question
          </h3>
          <div className="form-group">
            <label className="form-label">Question Text *</label>
            <textarea
              className="form-textarea"
              name="text"
              value={formData.text}
              onChange={handleChange}
              placeholder="Enter your interview question..."
              required
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px', marginBottom: '20px' }}>
            <div className="form-group">
              <label className="form-label">Question Type *</label>
              <select
                className="form-input"
                name="type"
                value={formData.type}
                onChange={handleChange}
              >
                <option value="Technical">Technical</option>
                <option value="General">General</option>
                <option value="Behavioral">Behavioral</option>
                <option value="Situational">Situational</option>
                <option value="Cultural Fit">Cultural Fit</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Required Answer Time (seconds) *</label>
              <input
                type="number"
                className="form-input"
                name="time"
                value={formData.time}
                onChange={handleChange}
                min="30"
                max="600"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Weight (1-10)</label>
              <input
                type="number"
                className="form-input"
                name="weight"
                value={formData.weight}
                onChange={handleChange}
                min="1"
                max="10"
              />
            </div>
          </div>
          <button 
            type="submit" 
            className="button button-primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? '⏳ Adding...' : '➕ Add Question'}
          </button>
        </form>
      )}
    </div>
  );
};

export default QuestionForm;
